import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { ArrowLeft, Check, Users, Wine, Sun, Palette, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const TYPES = [
  { id: 'date-night', name: 'Date Night', desc: 'Intimate dinner for two with curated courses', icon: Wine, price: 200 },
  { id: 'dinner-party', name: 'Dinner Party', desc: 'Elevated dining experience for your guests', icon: Users, price: 350 },
  { id: 'brunch', name: 'Weekend Brunch', desc: 'Relaxed morning spread with mimosas', icon: Sun, price: 180 },
  { id: 'custom', name: 'Custom Event', desc: 'Tell us exactly what you need', icon: Palette, price: 250 },
];

const MENU_OPTS = ['Filet Mignon', 'Lobster Tail', 'Pan-Seared Salmon', 'Rack of Lamb', 'Truffle Risotto', 'Caesar Salad', 'Chocolate Souffl\u00e9', 'Cr\u00e8me Br\u00fbl\u00e9e', 'Cheese Board', 'Caprese Salad'];

export default function InHomeBooking() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ booking_type: '', date: '', time: '19:00', guests: 2, menu_preferences: [], special_requests: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const selectedType = TYPES.find(t => t.id === form.booking_type);
  const totalPrice = selectedType ? selectedType.price + Math.max(0, form.guests - 2) * 25 : 0;

  const toggleMenu = (m) => {
    setForm(f => ({ ...f, menu_preferences: f.menu_preferences.includes(m) ? f.menu_preferences.filter(x => x !== m) : [...f.menu_preferences, m] }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await API.post('/in-home-bookings', form);
      setDone(true);
      toast.success('Booking submitted!', { description: 'Chef Kamusa will review your request shortly' });
    } catch {
      toast.error('Failed to submit booking');
    }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="fade-in max-w-lg mx-auto text-center py-20" data-testid="booking-success">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-green-600" /></div>
        <h1 className="text-2xl font-extrabold text-[#222] font-[Manrope]">Booking Submitted!</h1>
        <p className="text-[#717171] mt-2">Chef Kamusa will review your request. Pay ${totalPrice} via Zelle or Venmo once approved.</p>
        <Button onClick={() => navigate('/bookings')} className="mt-6 bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg btn-press" data-testid="view-bookings-after-submit">View My Bookings</Button>
      </div>
    );
  }

  return (
    <div className="fade-in max-w-2xl mx-auto" data-testid="in-home-booking-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[#717171] hover:text-[#222] text-sm font-medium mb-6" data-testid="back-from-booking">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
      </button>

      <h1 className="text-3xl font-extrabold tracking-tight text-[#222] font-[Manrope]">Book In-Home Chef</h1>
      <p className="mt-2 text-[#717171] mb-8">A private dining experience in your residence</p>

      {/* Type Selection */}
      <div className="space-y-3 mb-8">
        <Label className="text-sm font-semibold uppercase tracking-wide text-[#717171]">Experience Type</Label>
        <div className="grid sm:grid-cols-2 gap-3">
          {TYPES.map(({ id, name, desc, icon: Icon, price }) => (
            <button key={id} onClick={() => setForm(f => ({ ...f, booking_type: id }))} className={`text-left rounded-xl border-2 p-5 transition-all ${form.booking_type === id ? 'border-[#E05A47] bg-[#E05A47]/5' : 'border-[#EBEBEB] hover:border-[#222]/20'}`} data-testid={`type-${id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${form.booking_type === id ? 'bg-[#E05A47]/10 text-[#E05A47]' : 'bg-[#F7F7F7] text-[#717171]'}`}>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#222] font-[Manrope]">{name}</p>
                  <p className="text-xs text-[#717171] mt-0.5">{desc}</p>
                  <p className="text-sm font-bold text-[#222] mt-1.5">From ${price}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {form.booking_type && (
        <div className="space-y-6">
          {/* Date & Time */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-[#222]">Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="mt-1.5 rounded-lg border-[#EBEBEB] h-11" data-testid="booking-date" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#222]">Time</Label>
              <Input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className="mt-1.5 rounded-lg border-[#EBEBEB] h-11" data-testid="booking-time" />
            </div>
          </div>

          {/* Guests */}
          <div>
            <Label className="text-sm font-medium text-[#222]">Number of Guests</Label>
            <div className="flex items-center gap-4 mt-1.5">
              <button onClick={() => setForm(f => ({ ...f, guests: Math.max(1, f.guests - 1) }))} className="w-10 h-10 rounded-full border border-[#EBEBEB] flex items-center justify-center hover:bg-[#F7F7F7]" data-testid="guests-minus"><Minus className="w-4 h-4" /></button>
              <span className="text-lg font-bold w-8 text-center" data-testid="guests-count">{form.guests}</span>
              <button onClick={() => setForm(f => ({ ...f, guests: Math.min(20, f.guests + 1) }))} className="w-10 h-10 rounded-full border border-[#EBEBEB] flex items-center justify-center hover:bg-[#F7F7F7]" data-testid="guests-plus"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Menu Preferences */}
          <div>
            <Label className="text-sm font-medium text-[#222]">Menu Preferences</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {MENU_OPTS.map(m => (
                <button key={m} onClick={() => toggleMenu(m)} className={`px-3.5 py-2 rounded-full border text-sm font-medium transition-all ${form.menu_preferences.includes(m) ? 'border-[#E05A47] bg-[#E05A47]/10 text-[#E05A47]' : 'border-[#EBEBEB] text-[#717171]'}`} data-testid={`menu-${m.toLowerCase().replace(/\s+/g, '-')}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Special Requests */}
          <div>
            <Label className="text-sm font-medium text-[#222]">Special Requests</Label>
            <textarea value={form.special_requests} onChange={e => setForm(f => ({ ...f, special_requests: e.target.value }))} rows={3} placeholder="Occasion details, allergies, etc." className="mt-1.5 w-full rounded-lg border border-[#EBEBEB] px-4 py-3 text-sm focus:ring-2 focus:ring-[#E05A47] focus:border-transparent" data-testid="special-requests" />
          </div>

          {/* Price Summary */}
          <div className="bg-[#F7F7F7] rounded-xl p-5">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#717171]">{selectedType?.name} base</span>
              <span className="text-[#222]">${selectedType?.price}</span>
            </div>
            {form.guests > 2 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#717171]">Extra guests ({form.guests - 2} x $25)</span>
                <span className="text-[#222]">${(form.guests - 2) * 25}</span>
              </div>
            )}
            <div className="border-t border-[#EBEBEB] pt-2 mt-2 flex justify-between font-bold text-[#222]">
              <span>Estimated Total</span>
              <span className="font-[Manrope]">${totalPrice}</span>
            </div>
            <p className="text-xs text-[#E05A47] font-semibold mt-2">Payment: Zelle or Venmo after approval</p>
          </div>

          <Button onClick={handleSubmit} disabled={loading || !form.date} className="w-full bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold h-12 btn-press" data-testid="submit-booking-btn">
            {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Submit Booking Request'}
          </Button>
        </div>
      )}
    </div>
  );
}
